import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';
import useAuth from 'hooks/useAuth';

import { dispatch, useSelector } from 'store';
import { onCreateSimulation, onFetchSimulations, onOpenCreateSimulationForm, onOpenSimulationAlert } from 'store/slices/simulation';
import { openSnackbar } from 'store/slices/snackbar';
import { onParamsEdit } from 'store/slices/params';
import { gridSpacing } from 'store/constant';

import FormDialog from 'ui-component/dialogs/FormDialog';
import { Simulation, defaultSimulation } from 'models/Simulation';

import { Button, CircularProgress, Grid, Radio } from '@mui/material';

import ChooseType from '../../../ChooseType';
import DetailsForm from './DetailsForm';
import TabPanel from 'ui-component/TabPanel';
import nameof from 'utils/form';

interface Error {
    field: string;
    message: string;
}

const AddSimulation: FC = () => {
    const { openCreateSimulationForm } = useSelector((state) => state.simulation);

    const handleCancel = () => {
        dispatch(onOpenSimulationAlert(false));
        dispatch(onOpenCreateSimulationForm(false));
    };

    return (
        <FormDialog
            open={openCreateSimulationForm}
            dialogTitle="Crea nuova simulazione"
            form={<AddForm />}
            sx={{ '.MuiDialogContent-root': { overflow: 'hidden' } }}
            cancelButtonProps={{ children: 'Cancella' }}
            onClose={handleCancel}
        />
    );
};

export default AddSimulation;

const AddForm: FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const { isLoading, openCreateSimulationForm } = useSelector((state) => state.simulation);

    const [tabIndex, setTabIndex] = useState<number>(0);
    const [createType, setCreateType] = useState<number>(0);
    const [error, setError] = useState<Error | null>(null);
    const [newSimulation, setNewSimulation] = useState<Simulation>(defaultSimulation);

    const handleChangeTab = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setTabIndex(parseInt(event.target.value, 10));
    };

    const handleChangeCreateType = useCallback((type: number) => {
        setCreateType(type);
    }, []);

    const handleChangeDetails = useCallback((simulation: Simulation) => {
        setNewSimulation(simulation);
    }, []);

    const handleBack = () => {
        if (tabIndex === 0) return;
        setTabIndex((prev) => prev - 1);
    };

    const handleContinue = async () => {
        if (tabIndex === 1) {
            if (!user) return;

            if (!newSimulation.name) {
                setError({ field: nameof<Simulation>('name'), message: 'SIMULATION_NAME_IS_REQUIRED' });
                return;
            }

            try {
                const res = await dispatch(onCreateSimulation(user.tenantId, user.userId, user.userName, newSimulation));
                if (res) {
                    await dispatch(onFetchSimulations(user.tenantId));
                    dispatch(onOpenCreateSimulationForm(false));
                    setNewSimulation(defaultSimulation);
                    dispatch(onParamsEdit(true));
                } else {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: t('SIMULATION_CREATION_FAILED'),
                            variant: 'alert',
                            alert: {
                                color: 'error'
                            },
                            close: false
                        })
                    );
                }
            } catch (error) {
                console.log(error);
            }
            return;
        }

        setTabIndex((prev) => prev + 1);
    };

    useEffect(() => () => setNewSimulation(defaultSimulation), [openCreateSimulationForm]);

    return (
        <Grid container spacing={gridSpacing}>
            <Grid item xs={12}>
                <SwipeableViews axis="x" index={tabIndex}>
                    <TabPanel value={tabIndex} index={0}>
                        <ChooseType
                            createType={createType}
                            firstTypeOption={t('DALLA_SITUAZIONE_ATTUALE')}
                            secondTypeOption={t('DA_SIMULAZIONE_ESISTENTE')}
                            onChange={handleChangeCreateType}
                        />
                    </TabPanel>
                    <TabPanel value={tabIndex} index={1}>
                        <DetailsForm createType={createType} error={error} newSimulation={newSimulation} onChange={handleChangeDetails} />
                    </TabPanel>
                </SwipeableViews>
            </Grid>
            <Grid item container xs={12} justifyContent="space-between">
                <Grid item>
                    <Button disabled={tabIndex === 0} variant="outlined" onClick={handleBack}>
                        Indietro
                    </Button>
                </Grid>

                <Grid item>
                    <Radio size="small" value={0} checked={tabIndex === 0} name="0" onChange={handleChangeTab} />
                    <Radio size="small" value={1} checked={tabIndex === 1} name="1" onChange={handleChangeTab} />
                </Grid>

                <Grid item>
                    <Button variant="contained" onClick={handleContinue}>
                        {tabIndex === 1 &&
                            (isLoading ? (
                                <CircularProgress color="inherit" sx={{ width: '20px !important', height: '20px !important' }} />
                            ) : (
                                'Salva'
                            ))}
                        {tabIndex !== 1 && 'Continua'}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};
